import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { PropTypes } from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Loader } from '@ge/components/loader';
import { Tbody, Td, Tr } from '@ge/components/table';
import { useTableFactories } from '@ge/components/table/use-table-factories';
import { Placeholders } from '@ge/models/constants';

import { ReportWidgetTable } from '../../tables/report-widget-table';

import {
  ReferencePeriodTableColumns,
  referencePeriodTableDefs,
} from './reference-period-table-cols';

dayjs.extend(isoWeek);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${({ summaryColumn }) =>
    summaryColumn &&
    `
    ${Tbody} ${Tr} ${Td}:last-of-type {
      font-weight: 500;
    }
  `}

  ${({ theme }) =>
    `${Tbody} ${Tr}:last-of-type ${Td} {
      border-top: 1px solid ${theme.createReport.widget.productionTable.rowBorderColor};
      font-weight: 700;
    }
  `}
`;

export const ReferencePeriodTableWidget = ({
  name,
  columns,
  data = [],
  isEditing,
  isInteractive,
  onRowSelectionChange,
}) => {
  const { t, ready } = useTranslation(['reporting.widgets']);
  // initialize all rows being selected
  const [rowsSelected] = useState(() => data.map(({ id }) => id));

  useEffect(() => {
    onRowSelectionChange(rowsSelected);
  }, [onRowSelectionChange, rowsSelected]);

  const cellValueMapFn = useCallback(
    (cellValue) => {
      return {
        [ReferencePeriodTableColumns.REFERENCE_PERIOD]:
          cellValue.referencePeriod === ReferencePeriodTableColumns.REFERENCE_PERIOD_TOTAL
            ? t('reference_table.reference_period_totals', 'Reference Period Totals')
            : cellValue.referencePeriod,
        [ReferencePeriodTableColumns.AVAILABLE_TIME]: cellValue.availableTime
          ? cellValue.availableTime
          : Placeholders.DASH,
        [ReferencePeriodTableColumns.UNAVAILABLE_TIME]: cellValue.unavailableTime
          ? cellValue.unavailableTime
          : Placeholders.DASH,
        [ReferencePeriodTableColumns.PRODUCTION]: cellValue.actualProduction
          ? cellValue.actualProduction
          : Placeholders.DASH,
        [ReferencePeriodTableColumns.LOST_PRODUCTION]: cellValue.lostProduction
          ? cellValue.lostProduction
          : Placeholders.DASH,
        [ReferencePeriodTableColumns.CONTRACTUAL_AVAILABILITY]: cellValue.contractualAvailability
          ? cellValue.contractualAvailability
          : Placeholders.DASH,
      };
    },
    [t],
  );

  // Table factories
  const [columnGroupFactory, columnFactory, cellFactory] = useTableFactories({
    t,
    columnDefs: referencePeriodTableDefs,
    cellValueMapFn,
  });

  if (!ready) {
    return <Loader />;
  }

  return (
    <Container>
      <ReportWidgetTable
        key={name}
        compressed
        noTitles
        scrollable={isInteractive}
        transparent
        columnGroupFactory={columnGroupFactory}
        columnFactory={columnFactory}
        cellFactory={cellFactory}
        columns={columns}
        values={data}
        onValueSelect={() => null}
        rowsSelected={rowsSelected}
        rowKeyProperty={'id'}
        isEditing={isEditing}
        variableHeight={true}
        vAlign={'middle'}
      />
    </Container>
  );
};

ReferencePeriodTableWidget.propTypes = {
  name: PropTypes.string.isRequired,
  data: PropTypes.instanceOf(Object),
  columns: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  isEditing: PropTypes.bool,
  isInteractive: PropTypes.bool,
  onRowSelectionChange: PropTypes.func.isRequired,
};
